'use client';

import { IResize, useResize } from 'components/hooks/useResize';
import { Banner } from 'data/interfaces/Common';
import Cookies from 'js-cookie';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, createContext, useContext, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { AppTheme, CookiesType, ModalType, SnackbarType } from 'types/enums';
import { SnackbarData } from 'types/types';

export interface AppContextState {
  isMobile: boolean;
  setIsMobile: (t: boolean) => void;

  theme: AppTheme;
  setTheme: (theme: AppTheme) => void;

  snackbar: SnackbarData | null;
  showSnackbar: (text: string, type: SnackbarType, timeout?: number) => void;

  modal: ModalType | null;
  modalArguments: any;
  showModal: <T>(type: ModalType, args?: T) => void;
  hideModal: () => void;
  modalNonSkippable: boolean;
  snapPoints: number;
  setSnapPoints: Dispatch<SetStateAction<number>>;
  bottomSheet: ModalType | null;
  bottomSheetArguments: any;
  showBottomSheet: <T>(type: ModalType, args?: T, snapPoints?: number) => void;
  hideBottomSheet: () => void;
  hideHeader: boolean;
  setHideHeader: Dispatch<SetStateAction<boolean>>;
  isNavBarActive: boolean;
  setNavBarActive: Dispatch<SetStateAction<boolean>>;

  isOverlayShown?: boolean;
  showOverlay: () => void;
  hideOverlay: () => void;
  size: IResize;
  isMaiaInit: boolean;
  setIsMaiaInit: Dispatch<SetStateAction<boolean>>;

  banners: Banner[];
  setBanners: Dispatch<SetStateAction<Banner[]>>;
}

const ModalsBottomSheet: ModalType[] = [];

const AppContext = createContext<Partial<AppContextState>>({});

interface Props {
  children: React.ReactNode;
  token?: string;
  isMobile: boolean;
  theme?: AppTheme;
  banners: Banner[];
}

export function AppWrapper(props: Props) {
  const [isMobile, setIsMobile] = useState<boolean>(props.isMobile);
  const [snackbar, setSnackbar] = useState<SnackbarData | null>(null);
  const [modal, setModal] = useState<ModalType | null>(null);
  const [modalArguments, setModalArguments] = useState<any>(null);
  const [bottomSheetArguments, setBottomSheeetArguments] = useState<any>(null);
  const [bottomSheet, setBottomSheet] = useState<ModalType | null>(null);
  const [isOverlayShown, setIsOverlayShown] = useState<boolean>(false);
  const [modalNonSkippable] = useState<boolean>(false);
  const [hideHeader, setHideHeader] = useState<boolean>(false);
  const [snapPoints, setSnapPoints] = useState<number>(null);
  const [isNavBarActive, setNavBarActive] = useState<boolean>(false);
  const searchParams = useSearchParams();
  const [theme, setThemeState] = useState<AppTheme>(
    (searchParams.get('theme') as AppTheme) || /*props.theme||AppTheme.Light*/ AppTheme.Dark,
  );
  const size = useResize();
  const [isMaiaInit, setIsMaiaInit] = useState<boolean>(false);
  const [banners, setBanners] = useState<Banner[]>(props.banners ?? []);

  useEffect(() => {
    setBanners(props.banners ?? []);
  }, [props.banners]);

  const setTheme = (theme: AppTheme) => {
    Cookies.set(CookiesType.theme, theme);
    setThemeState(theme);
  };

  const showSnackbar = (text: string, type: SnackbarType, timeout?: number) => {
    setSnackbar({ text, type });
    setTimeout(() => {
      setSnackbar(null);
    }, timeout || 2000);
  };

  const showModal = <T,>(type: ModalType, args?: T) => {
    if (props.isMobile && ModalsBottomSheet.includes(type)) {
      showBottomSheet(type, args);
      return;
    }
    ReactModal.setAppElement('body');
    setModalArguments(args);
    setModal(type);
    if (bottomSheet) {
      hideBottomSheet();
    }
  };

  const hideModal = () => {
    if (bottomSheet) {
      hideBottomSheet();
      // return
    }
    setModal(null);
    setModalArguments(null);
  };

  const showBottomSheet = <T,>(type: ModalType, props?: T, snapPoints?: number) => {
    setSnapPoints(snapPoints || null);
    ReactModal.setAppElement('body');
    setBottomSheeetArguments(props);
    setBottomSheet(type);
  };

  const hideBottomSheet = () => {
    setBottomSheet(null);
  };

  const value: AppContextState = {
    isMobile,
    setIsMobile,

    theme,
    setTheme,

    snackbar,
    showSnackbar,

    modal,
    modalArguments,
    showModal,
    hideModal,
    modalNonSkippable,

    bottomSheet,
    bottomSheetArguments,
    showBottomSheet,
    hideBottomSheet,
    snapPoints,

    hideHeader,
    setHideHeader,
    isNavBarActive,
    setNavBarActive,

    isOverlayShown,
    showOverlay: () => {
      setIsOverlayShown(true);
    },
    hideOverlay: () => {
      setIsOverlayShown(false);
    },
    size,
    setSnapPoints,
    isMaiaInit,
    setIsMaiaInit,

    banners,
    setBanners,
  };

  return <AppContext.Provider value={value}>{props.children}</AppContext.Provider>;
}

/** базовый контекст для неспецифичных сущностей */
export function useAppContext() {
  return useContext(AppContext);
}
